/**
 * JsonValueDisplay Component
 *
 * Reusable component for displaying JSON values with a popover viewer.
 * Shows compact preview (e.g., "{3 keys}") in a clickable tag that opens
 * a popover with a full ReactJson tree view.
 */

import React from 'react'
import { Popover, Tag, Typography } from 'antd'
import { CodeOutlined } from '@ant-design/icons'
import ReactJson from '@microlink/react-json-view'
import { formatJsonPreview, isJsonValue } from '../utils/jsonFormatters'

const { Text } = Typography

export interface JsonValueDisplayProps {
  /** The JSON value to display */
  value: unknown
  /** Maximum height for the popover content (default: 400px) */
  maxHeight?: number
  /** ReactJson collapse level (default: 2) */
  collapsed?: number
}

/**
 * Display a JSON value with compact preview and click-to-expand popover.
 *
 * @example
 * // Basic usage
 * <JsonValueDisplay value={{ foo: 1, bar: 2 }} />
 *
 * @example
 * // With custom settings
 * <JsonValueDisplay
 *   value={someJsonData}
 *   maxHeight={300}
 *   collapsed={1}
 * />
 */
export function JsonValueDisplay({
  value,
  maxHeight = 400,
  collapsed = 2,
}: JsonValueDisplayProps): React.ReactElement {
  // For non-JSON values, just render as text
  if (!isJsonValue(value)) {
    if (value === null || value === undefined) {
      return <Text type="secondary">-</Text>
    }
    return <Text>{String(value)}</Text>
  }

  const preview = formatJsonPreview(value)

  return (
    <Popover
      content={
        <div style={{ maxWidth: 500, maxHeight, overflow: 'auto' }}>
          <ReactJson
            src={value as object}
            theme="rjv-default"
            collapsed={collapsed}
            displayDataTypes={false}
            enableClipboard
            name={false}
          />
        </div>
      }
      trigger="click"
      placement="bottomLeft"
    >
      {/* Wrapper div stops click from propagating to table row handler */}
      {/* This ensures clicking the JSON tag opens the popover, not the row drawer */}
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Allow Enter/Space to trigger popover but stop propagation
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
          }
        }}
        style={{ display: 'inline-block' }}
      >
        <Tag
          icon={<CodeOutlined />}
          style={{ cursor: 'pointer' }}
        >
          {preview}
        </Tag>
      </div>
    </Popover>
  )
}

export default JsonValueDisplay
